import { chatEE } from '@/layout/events/chat-event-emitter';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Member } from '../../models/member';
import { PERMISSION_KEYS, type PermissionAction, type PermissionOverrideItemDto } from '../../models/permission';
import { useUpdatePermissionOverrides } from '../../queries/permission.mutations';
import { useMemberOverrides } from '../../queries/permission.queries';

const PERMISSION_ACTIONS: PermissionAction[] = ['INHERIT', 'ALLOW', 'DENY'];

interface PermissionDialogState {
  open: boolean;
  convId: string | null;
  member: Member | null;
}

interface PermissionConfigDialogContentProps {
  convId: string;
  member: Member;
  onClose: () => void;
}

const PermissionConfigDialogContent: FC<PermissionConfigDialogContentProps> = ({ convId, member, onClose }) => {
  const { t } = useTranslation();

  const { data: overridesData, isLoading } = useMemberOverrides(convId, member.userId);
  const { mutate: updateOverrides, isPending } = useUpdatePermissionOverrides(convId, member.userId);

  const [permissions, setPermissions] = useState<Record<string, PermissionAction>>({});

  useEffect(() => {
    if (overridesData?.overrides) {
      const map: Record<string, PermissionAction> = {};
      overridesData.overrides.forEach((o) => {
        map[o.permissionKey] = o.action;
      });
      Object.values(PERMISSION_KEYS).forEach((key) => {
        if (!map[key]) map[key] = 'INHERIT';
      });
      setPermissions(map);
    }
  }, [overridesData]);

  const handleChange = (key: string, action: PermissionAction) => {
    setPermissions((prev) => ({ ...prev, [key]: action }));
  };

  const hasChanges = useMemo(() => {
    if (!overridesData?.overrides) {
      return Object.values(permissions).some((action) => action !== 'INHERIT');
    }
    const original: Record<string, PermissionAction> = {};
    overridesData.overrides.forEach((o) => {
      original[o.permissionKey] = o.action;
    });
    Object.values(PERMISSION_KEYS).forEach((key) => {
      if (!original[key]) original[key] = 'INHERIT';
    });
    return Object.keys(permissions).some((k) => permissions[k] !== original[k]);
  }, [permissions, overridesData]);

  const handleSave = () => {
    const items: PermissionOverrideItemDto[] = Object.entries(permissions).map(([permissionKey, action]) => ({
      permissionKey,
      action
    }));
    updateOverrides(items, { onSuccess: onClose });
  };

  return (
    <Dialog open fullWidth maxWidth='sm' onClose={onClose}>
      <DialogTitle>
        {t('conversations.rbac.permissionConfig')}
        <Typography variant='body2' color='text.secondary'>
          {member.user?.fullName || member.userId}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <List dense>
            {Object.values(PERMISSION_KEYS).map((key) => (
              <ListItem key={key} sx={{ py: 1 }}>
                <ListItemText primary={t(`conversations.rbac.permissions.${key}`)} sx={{ flex: 1 }} />
                <Select
                  size='small'
                  value={permissions[key] || 'INHERIT'}
                  onChange={(e) => handleChange(key, e.target.value as PermissionAction)}
                  sx={{ minWidth: 120 }}
                >
                  {PERMISSION_ACTIONS.map((action) => (
                    <MenuItem key={action} value={action}>
                      {t(`conversations.rbac.action.${action.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={isPending || !hasChanges || isLoading}>
          {isPending ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PermissionConfigDialog: FC = () => {
  const [state, setState] = useState<PermissionDialogState>({
    open: false,
    convId: null,
    member: null
  });

  useEffect(() => {
    const handleOpen = ({ convId, member }: { convId: string; member: Member }) => {
      setState({ open: true, convId, member });
    };
    chatEE.on('permission-config-open', handleOpen);
    return () => {
      chatEE.off('permission-config-open', handleOpen);
    };
  }, []);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  if (!state.open || !state.convId || !state.member) return null;

  return <PermissionConfigDialogContent convId={state.convId} member={state.member} onClose={handleClose} />;
};
